import type {
  RequestContactArgs,
  UseDirectDealsContact,
} from '@openx/components-direct-deals/lib/utils/context/types.ts';
import { getInitialContactValue, LOCAL_STORAGE_KEY } from '@openx/utils/directDealContactValue.ts';
import { useMutation } from '@tanstack/react-query';
import { addDays, isAfter } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ContactPublisherProfileDocument,
  type ContactPublisherProfileMutationVariables,
} from '../../../graphql/mutation/directDeals/ContactPublisherProfile.generated.tsx';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types.ts';

export const useDirectDealsContact: UseDirectDealsContact = (initialPublisherId = '') => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [initialValue] = useState<string>(getInitialContactValue(initialPublisherId));
  const [canContactAgain, setCanContactAgain] = useState<boolean>(
    !initialValue ? true : isAfter(new Date(), new Date(initialValue))
  );

  const { mutateAsync: contactMutation } = useMutation({
    mutationFn: (variables: ContactPublisherProfileMutationVariables, { client }) =>
      getGraphQLRequestFromClient(client)(ContactPublisherProfileDocument, variables),
  });

  const onErrorCallback = useCallback(() => {
    setCanContactAgain(true);
    localStorage.removeItem(LOCAL_STORAGE_KEY);

    enqueueSnackbar(t('Something went wrong. Please try again later and contact us if the issue persists.'), {
      variant: 'error',
    });
  }, [enqueueSnackbar, t]);

  const requestContact = async ({ publisherId, publisherName, data, callback }: RequestContactArgs) => {
    try {
      const res = await contactMutation({
        id: publisherId,
        input: data,
      });

      if (!res.publisherProfileContactRequest.success) {
        throw new Error('Contact request failed');
      }

      enqueueSnackbar(
        t(
          'Your request to start a Deal with {publisherName} has been successfully sent. A dedicated representative will review your proposal and reach out to you shortly.',
          {
            publisherName,
          }
        ),
        { variant: 'success' }
      );

      localStorage.setItem(LOCAL_STORAGE_KEY + publisherId, addDays(new Date(), 1).toISOString());
      setCanContactAgain(false);
      callback?.();
    } catch {
      onErrorCallback();
    }
  };

  return {
    requestContact,
    canContactAgain,
  };
};
